/*
 * Copyright ©2020. Open Digital Solutions, Novi Sad. Sva prava zadržana.
 * Pravo da se koristi, kopira, modifikuje i distribuira ovaj softver i njegova dokumentacija
 * u bilo koje svrhe, bez naknade ili bez potpisanog sporazuma sa vlasnikom softvera, nije dozvoljeno.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppService } from './app.service';
import { Pageable } from 'src/app/pageable';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import * as printJS from 'print-js';
import { Page } from 'src/app/page';

@Injectable({
   providedIn: 'root',
})
export class OciService {
   constructor(
      private http: HttpClient,
      private appService: AppService,
   ) {}

   getDocumentsForCase(pageable: Pageable, filterValue: string, subjectId: string, caseId: string, csrId: any) {
      return this.http.post(
         this.appService.getDocManagerUrl() + environment.ociGetAllDocumentsForCase + pageable.pageNumber + '/' + pageable.pageSize + '/' + subjectId + '/' + caseId + '/' + csrId,
         filterValue,
      ) as Observable<any>;
   }

   uploadFileForSubjectsCase(fileToUpload: File, subjectId: string, caseId: string, docType: Number, alfrescoDocId?: string) {
      const formData: FormData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);

      if (alfrescoDocId) {
         formData.append('alfrescoDocId', alfrescoDocId);
      }

      return this.http.post(this.appService.getDocManagerUrl() + environment.ociUploadDocument + subjectId + '/' + caseId + '/' + docType, formData, {
         responseType: 'text',
      }) as Observable<any>;
   }

   previewDocument(documentId, format) {
      let headers = new HttpHeaders();
      headers = headers.set('Accept', format);

      return this.http.get(this.appService.getDocManagerUrl() + environment.ociViewDocument + documentId, {
         headers,
         responseType: 'blob',
         observe: 'response',
      }) as Observable<any>;
   }

   downloadDocument(documentId: string, fileName: string, format) {
      let headers = new HttpHeaders();
      headers = headers.set('Accept', format);
      return this.http
         .get(this.appService.getDocManagerUrl() + environment.ociDownloadDocument + documentId, {
            headers,
            responseType: 'blob',
         })
         .subscribe(result => {
            const file = new Blob([result], { type: result.type });
            const fileURL = URL.createObjectURL(file);
            const anchor = document.createElement('a');
            anchor.download = fileName;
            anchor.href = fileURL;
            anchor.click();
         });
   }

   downloadDocumentKeyName(documentKeyName: string, fileName: string) {
      let headers = new HttpHeaders();
      return this.http
         .post(this.appService.getDocManagerUrl() + environment.ociDownloadDocumentKeyName, documentKeyName, {
            headers,
            responseType: 'blob',
         })
         .subscribe(result => {
            const file = new Blob([result], { type: result.type });
            const fileURL = URL.createObjectURL(file);
            const anchor = document.createElement('a');
            anchor.download = fileName;
            anchor.href = fileURL;
            anchor.click();
         });
   }

   printDocument(documentId: string, fileName: string) {
      const headers = new HttpHeaders();

      this.http.get(this.appService.getDocManagerUrl() + environment.ociDownloadDocument + documentId, { responseType: 'blob', headers }).subscribe(data => {
         const file = new Blob([data], { type: 'application/pdf' });
         const fileURL = URL.createObjectURL(file);
         printJS(fileURL);
      });
   }

   documentSendExpedition(sendingDocumentDto: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json;',
      });
      return this.http.post(this.appService.getDocManagerUrl() + environment.ociSendToExpedition, sendingDocumentDto) as Observable<any>;
   }

   checkIfFileExistIn(caseId: any, fileName: any) {
      return this.http.get(this.appService.getDocManagerUrl() + environment.ociFileExistInCase + caseId + '/' + encodeURIComponent(fileName)) as Observable<any>;
   }

   downloadTemplate(body: any) {
      let headers = new HttpHeaders();
      headers = headers.set('Accept', 'application/pdf');
      return this.http.post(this.appService.getDocManagerUrl() + environment.ociDownloadTemplate, body, { headers, responseType: 'blob' }).subscribe(data => {
         const parts = [];
         parts.push('\uFEFF');
         parts.push(data);
         const fileURL = URL.createObjectURL(data);
         const a = document.createElement('a');
         document.body.appendChild(a);
         a.setAttribute('style', 'display: none');
         a.href = fileURL;
         a.download = body.fileName;
         a.click();
         window.URL.revokeObjectURL(fileURL);
         a.remove();
      });
   }

   getWorkerDocuments(workerId: any, pageable: Pageable, filterValue: any) {
      return this.http.post(this.appService.getDocManagerUrl() + environment.ociGetWorkerDocuments + pageable.pageNumber + '/' + pageable.pageSize + '/' + workerId, filterValue) as Observable<any>;
   }

   uploadFileForUserEvaluation(fileToUpload: File, workerId: any, alfrescoDocId?: string) {
      const formData: FormData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);

      if (alfrescoDocId) {
         formData.append('alfrescoDocId', alfrescoDocId);
      }

      return this.http.post(this.appService.getDocManagerUrl() + environment.ociUploadEvaluationDocument + workerId, formData) as Observable<any>;
   }

   getDocumentsForSubject(pageable: Pageable, filterValue: string, subjectId: string, csrId: any): Observable<Page<Document>> {
      return this.http.post(
         this.appService.getDocManagerUrl() + environment.ociGetSubjectDocuments + pageable.pageNumber + '/' + pageable.pageSize + '/' + subjectId + '/' + csrId,
         filterValue,
      ) as Observable<any>;
   }

   uploadFileForSubject(fileToUpload: File, id: string, docType: Number, alfrescoDocId?: string) {
      const formData: FormData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);

      if (alfrescoDocId) {
         formData.append('alfrescoDocId', alfrescoDocId);
      }

      return this.http.post(this.appService.getDocManagerUrl() + environment.ociUploadSubjectDocument + id + '/' + docType, formData) as Observable<any>;
   }

   getAssessmentsForCase(caseId: any) {
      return this.http.get(this.appService.getDocManagerUrl() + environment.ociGetAssessmentsForCase + caseId) as Observable<any>;
   }

   getSubmissionDocuments(submissionId: any, pageable: Pageable, filterValue: string): Observable<any> {
      return this.http.post(
         this.appService.getDocManagerUrl() + environment.ociGetSubmissionDocuments + submissionId + '/' + pageable.pageNumber + '/' + pageable.pageSize,
         filterValue,
      ) as Observable<any>;
   }

   handleSignature(handleSignature: any) {
      return this.http.post(this.appService.getDocManagerUrl() + environment.ociHandleSignature, handleSignature) as Observable<any>;
   }

   getDocumentsForServiceProviderSubject(pageable: Pageable, filterValue: string, subjectId: string, serviceProviderId: any): Observable<Page<Document>> {
      return this.http.post(
         this.appService.getDocManagerUrl() + environment.ociGetServiceProviderSubjectDocuments + pageable.pageNumber + '/' + pageable.pageSize + '/' + subjectId + '/' + serviceProviderId,
         filterValue,
      ) as Observable<any>;
   }

   getDocumentsForCaseWithReferenceNumber(caseId: number, withReferenceNumber: boolean): Observable<any> {
      return this.http.get(this.appService.getDocManagerUrl() + environment.ociGetDocumentsForCaseWithReferenceNumber + caseId + '/' + withReferenceNumber) as Observable<any>;
   }

   sendDocumentToCsr(deliveryRequest: any): Observable<any> {
      return this.http.post(this.appService.getDocManagerUrl() + environment.ociSendDocumentToCsr, deliveryRequest) as Observable<any>;
   }  
}
